/**
 * メッセージ一覧の好み絞り込みの2群割当。
 *
 * | 群 | 割合 | メッセージ一覧 |
 * |---|---|---|
 * | control | 50% | 誰からのメッセージも届く（従来どおり） |
 * | filtered | 50% | 好みに合う女性からのメッセージだけ残す |
 *
 * ★オンボーディングは全員に見せる。群で出し分けない。
 *   好みが未登録だと BE が絞り込まずに全件返すため、filtered 群の半分が好み未登録だと
 *   control と区別できなくなる。絞り込みの効果を測るには全員が登録している必要がある。
 * ★この実験で読めるのは絞り込み単体の効果だけ。オンボーディング自体の効果は
 *   対照群が無いので読めない（導入前後の比較で見る）。
 *
 * ★★集計側（jambo-ds-effect-dashboard の `src/lib/prefExperiment.ts`）と
 *   salt・境界・演算子が同じであること。割り付けを記録していないので、ずれると群が
 *   まるごと入れ替わる。しかも画面は普通に描画されるため気付けない。
 */
import { PREFERENCE_ONBOARDING_EXPERIMENT } from "~/constants/experiments";
import { getExperimentBucket } from "~/lib/abTest";
import { captureError } from "~/lib/sentry/captureError";

export type PreferenceOnboardingArm = "control" | "filtered";

/**
 * 群の境界（バケットは 0〜99）。**上限は排他（bucket < UPPER）**。
 * control = 0〜49（50個）/ filtered = 50〜99（50個）。
 */
const CONTROL_UPPER = 50;

/**
 * 指定ユーザーが属する群を返す。
 *
 * ハッシュの算出に失敗した場合は control として扱う。実験のために既存の挙動を
 * 壊さないことを優先する（`isInTreatmentGroup` と同じ方針）。
 */
export const resolvePreferenceOnboardingArm = async (
  userId: string,
): Promise<PreferenceOnboardingArm> => {
  try {
    const bucket = await getExperimentBucket(
      PREFERENCE_ONBOARDING_EXPERIMENT,
      userId,
    );
    return bucket < CONTROL_UPPER ? "control" : "filtered";
  } catch (error) {
    captureError(error, "preferenceOnboardingExperiment: 群の算出に失敗");
    return "control";
  }
};

/** メッセージ一覧で好み絞り込みAPIを使う群か（#1124 から参照する） */
export const shouldUsePreferenceFilteredConversationList = async (
  userId: string,
): Promise<boolean> =>
  (await resolvePreferenceOnboardingArm(userId)) === "filtered";
